// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getQuoteLineBreakPrice from "@bg/App/Components/RfqQuoteDetails/GetQuoteBreakPrice";
import QuoteLineImageCarousel from "@bg/App/Components/RfqQuoteDetails/QuoteLineImageCarousel";
import deleteRelatedLine from "@bg/App/Handlers/RequestQuote/DeleteRelatedLine";
import updateLineInfo from "@bg/App/Handlers/RequestQuote/UpdateLineInfo";
import useAccessibleSubmit from "@bg/App/Hooks/useAccessibleSubmit";
import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { CustomAppState } from "@bg/App/Store/Reducers";
import ProductAvailability from "@bg/Overrides/Components/ProductAvailability";
import ProductDescription from "@bg/Overrides/Components/ProductDescription";
import TwoButtonModal from "@bg/Overrides/Components/TwoButtonModal";
import { productListLineStyles } from "@bg/Overrides/Widgets/MyListsDetails/MyListDetailsProductListLineStyles";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import updateQuoteLine from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/UpdateQuoteLine";
import translate from "@insite/client-framework/Translate";
import { QuoteLineModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText from "@insite/content-library/Components/SmallHeadingAndText";
import RfqQuoteDetailsQuotedPricing from "@insite/content-library/Widgets/RfqQuoteDetails/RfqQuoteDetailsQuotedPricing";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const QuoteLineQuantity = React.lazy(
    () =>
        import(`@${BLUEPRINT_NAME === "bp" ? "bg" : BLUEPRINT_NAME}/App/Components/RfqQuoteDetails/QuoteLineQuantity`),
);

interface OwnProps {
    quoteLine: QuoteLineModel;
    quote: QuoteModel;
    quoteLineClickHandler?: (line: QuoteLineModel) => void;
    groupName: string;
    parentLineId: string;
    editable?: boolean;
    isDisabled?: boolean;
}

const mapStateToProps = (state: CustomAppState) => ({
    addOnsGroupName: (getSettingsCollection(state).customSettings as any).addOnsGroupName as string,
});

const mapDispatchToProps = {
    updateLineInfo,
    deleteRelatedLine,
    updateQuoteLine,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProposedQuoteLinesStyles {
    relatedItemsWrapper?: InjectableCss;
    quantityGridItem?: InjectableCss;
    quotedPricingGridItem?: InjectableCss;
    quoteButtonGrid?: InjectableCss;
    addNotes?: LinkPresentationProps;
    removeQuoteLineLink?: LinkPresentationProps;
    notesGridItem?: InjectableCss;
    textAreaField?: TextAreaProps;
}

export const proposedQuoteLinesStylesStyles: ProposedQuoteLinesStyles = {
    quantityGridItem: {
        css: css`
            min-width: calc((100% - 150px) / 3);
            display: flex;
            justify-content: center;
            @media (max-width: 600px) {
                width: 50%;
                justify-content: flex-start;
                order: 2;
            }
        `,
    },
    quotedPricingGridItem: {
        css: css`
            flex-grow: 1;
            @media (max-width: 600px) {
                width: 100%;
                order: 1;
            }
        `,
    },
    quoteButtonGrid: {
        css: css`
            min-width: 150px;
            display: flex;
            justify-content: flex-end;
            align-items: flex-start;
            @media (max-width: 600px) {
                width: 50%;
                order: 3;
                min-width: unset;
            }
        `,
    },
    notesGridItem: {
        css: css`
            width: 100%;
            margin: 10px 0 0;
            text-align: right;
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            .notes-link {
                margin-left: auto;
                @media (max-width: 481px) {
                    margin-left: 0;
                }
            }
        `,
    },
    addNotes: linkIconStyleGenerator({
        icon: "Create",
        textSize: `${fontConfig.fontSize.xSmall}px`,
        iconSize: 14,
        additionalTypographyCss: css`
            line-height: ${fontConfig.lineHeight.singleLine};
        `,
    }),
    removeQuoteLineLink: linkIconStyleGenerator({
        icon: "Delete",
        textSize: `${fontConfig.fontSize.xSmall}px`,
        iconSize: 14,
        additionalTypographyCss: css`
            line-height: ${fontConfig.lineHeight.singleLine};
        `,
    }),
    textAreaField: {
        mergeCss: true,
        cssOverrides: {
            inputSelect: css`
                resize: none;
            `,
        },
    },
};

const styles = proposedQuoteLinesStylesStyles;

const ProposedRelatedQuoteLines = ({
    quote,
    quoteLine,
    groupName,
    updateLineInfo,
    editable = true,
    parentLineId,
    deleteRelatedLine,
    updateQuoteLine,
    isDisabled,
    addOnsGroupName,
}: Props) => {
    const [lineGroup, setLineGroup] = useState<any>();
    const [notes, setNotes] = useState("");
    const [lineQty, setLineQty] = useState(0);
    const [lineImages, setLineImages] = useState<any>();
    const [deleteLineModalIsOpen, setDeleteLineModalIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showNotes, setShowNotes] = useState(false);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (quoteLine && quoteLine.properties.groups) {
            setLineGroup(
                JSON.parse(quoteLine.properties.groups)?.filter(
                    (x: any) => x.groupName === groupName.toLowerCase() && x.parentId === parentLineId,
                )?.[0],
            );
        }
        if (quoteLine && quoteLine.properties) {
            let images = [] as any;
            if (quoteLine.properties.images) {
                images = JSON.parse(quoteLine.properties["images"]);
            } else {
                images = [{ id: quoteLine.productId, imagePath: quoteLine.smallImagePath }];
            }
            setLineImages(images);
        }
    }, [groupName, parentLineId, quoteLine]);

    useEffect(() => {
        if (lineGroup) {
            setNotes(lineGroup.notes);
            setShowNotes(!!lineGroup.notes);
        }
        if (quoteLine && lineGroup) {
            const lineQty =
                quoteLine.properties.isFixedQty === "true"
                    ? +quoteLine.properties.exactQty
                    : quoteLine.properties.minimumQty && +lineGroup?.qtyOrdered < +quoteLine.properties.minimumQty
                      ? +quoteLine.properties.minimumQty
                      : +lineGroup?.qtyOrdered;

            setLineQty(lineQty);
        }
    }, [quoteLine, lineGroup]);

    const toasterContext = useContext(ToasterContext);

    const qtyOrderedSubmitHandler = (value: string) => {
        const inputQty = Number.parseInt(value, 10);
        if (Number.isNaN(inputQty)) {
            return;
        }

        dispatch({
            type: "Pages/xRfqQuoteDetailsProposed/UpdateProductsQty",
            lineModel: {
                ...quoteLine,
                properties: {
                    ...quoteLine.properties,
                    originalGroupName: groupName,
                },
                qtyOrdered: inputQty === 0 ? 1 : inputQty,
                originalQty: lineGroup.qtyOrdered,
            } as QuoteLineModel,
        });
    };

    const deleteRelatedQuoteLine = () => {
        setIsDeleting(true);

        deleteRelatedLine({
            cartId: quote.id,
            originalGroupName: groupName,
            linesModel: [{ lineId: quoteLine.id, parentId: parentLineId }],
            reloadQuote: true,
            onComplete: result => {
                setDeleteLineModalIsOpen(false);
                setIsDeleting(false);
            },
        });
    };

    const LineQtyOrdered =
        quoteLine.properties.isFixedQty === "true"
            ? quoteLine.properties.exactQty
            : quoteLine.properties.minimumQty && +lineGroup?.qtyOrdered < +quoteLine.properties.minimumQty
              ? quoteLine.properties.minimumQty
              : lineGroup?.qtyOrdered;

    const {
        value: qtyOrdered,
        changeHandler: qtyOrderedChangeHandler,
        keyDownHandler: qtyOrderedKeyDownHandler,
        blurHandler: qtyOrderedBlurHandler,
    } = useAccessibleSubmit(LineQtyOrdered, qtyOrderedSubmitHandler);

    const toggleNotesClickHandler = () => {
        setShowNotes(!showNotes);
    };

    const notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };

    const notesBlurHandler = (e: any) => {
        if (e.currentTarget.value === lineGroup.notes) {
            return;
        }
        setShowNotes(!!e.currentTarget.value);
        updateLineInfo({
            cartId: quote.id,
            originalGroupName: groupName,
            linesModel: [
                {
                    lineId: quoteLine.id,
                    qtyOrdered: lineGroup.qtyOrdered,
                    sortOrder: lineGroup.sortOrder,
                    notes,
                    parentId: parentLineId,
                },
            ],
            reloadQuote: true,
        });
    };

    const toggleNotesHeading = showNotes
        ? translate("Hide Notes")
        : !lineGroup?.notes || lineGroup?.notes.length === 0
          ? translate("Add Notes")
          : translate("Show Notes");

    const getUniqueGroupLineTotal = () => {
        let total = 0;
        total =
            (getQuoteLineBreakPrice(
                quoteLine.pricing?.actualBreakPrices ?? null,
                lineQty,
                quoteLine.pricing?.unitNetPrice,
            ) *
                lineQty *
                100) /
                100 || 0;
        return total;
    };

    const allowRemoveAddOns =
        quote.properties["allowAddOnsItemRemoval"].toLowerCase() === "true" &&
        groupName.toLowerCase() === addOnsGroupName.toLowerCase();

    return (
        <StyledWrapper
            className="related-item"
            {...productListLineStyles.lineInnerContainer}
            data-test-selector={`quoteLine_${quoteLine.productId}_${quoteLine.unitOfMeasure}`}
        >
            <StyledWrapper {...productListLineStyles.leftSection}>
                <StyledWrapper {...productListLineStyles.imageGridItem} className="related-item">
                    <QuoteLineImageCarousel
                        productId={quoteLine.id}
                        productImages={lineImages}
                        productUrl={quoteLine.productUri}
                    />
                </StyledWrapper>

                <StyledWrapper {...productListLineStyles.descriptionGridItem}>
                    <ProductDescription
                        product={quoteLine}
                        lines={2}
                        lineHeight={22}
                        extendedStyles={productListLineStyles.productDescriptionStyles}
                    />
                    <StyledWrapper {...productListLineStyles.partNumbersGridItem}>
                        <ProductPartNumbers
                            productNumber={quoteLine.properties.sku || quoteLine.erpNumber}
                            manufacturerItem={quoteLine.manufacturerItem}
                            extendedStyles={productListLineStyles.productPartNumbersStyles}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...productListLineStyles.availabilityGridItem}>
                        <ProductAvailability
                            productId={quoteLine.productId!}
                            availability={quoteLine.availability!}
                            unitOfMeasure={quoteLine.unitOfMeasure}
                            trackInventory={quoteLine.trackInventory}
                            extendedStyles={productListLineStyles.productAvailabilityStyles}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...productListLineStyles.rightSection}>
                <StyledWrapper {...productListLineStyles.rightSectionTop}>
                    <StyledWrapper {...productListLineStyles.priceAndAvailabilityInnerContainer}>
                        <StyledWrapper {...styles.quotedPricingGridItem}>
                            <RfqQuoteDetailsQuotedPricing
                                quote={quote}
                                quoteLine={quoteLine}
                                extendedStyles={{
                                    priceCell: {
                                        css: css`
                                            text-align: center;
                                            width: 50%;
                                            @media (max-width: 600px) {
                                                text-align: right !important;
                                            }
                                        `,
                                    },
                                }}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.quantityGridItem}>
                            {editable ? (
                                <React.Suspense fallback="...">
                                    <QuoteLineQuantity
                                        qtyOrdered={qtyOrdered}
                                        quoteLine={quoteLine}
                                        quote={quote}
                                        qtyOrderedChangeHandler={qtyOrderedChangeHandler}
                                        qtyOrderedKeyDownHandler={qtyOrderedKeyDownHandler}
                                        qtyOrderedBlurHandler={qtyOrderedBlurHandler}
                                        onSubmit={qtyOrderedSubmitHandler}
                                        isDisabled={isDisabled}
                                    />
                                </React.Suspense>
                            ) : (
                                <Typography
                                    css={css`
                                        text-align: center;
                                    `}
                                >
                                    <legend>{translate("Qty")}</legend>
                                    {lineQty}
                                </Typography>
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.quoteButtonGrid}>
                            <Typography {...productListLineStyles.priceTotal}>
                                <legend>{translate("Total")}</legend>
                                <LocalizedCurrency amount={getUniqueGroupLineTotal()} />
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                {editable ? (
                    <StyledWrapper {...styles.notesGridItem}>
                        {allowRemoveAddOns && !isDisabled && (
                            <Link {...styles.removeQuoteLineLink} onClick={() => setDeleteLineModalIsOpen(true)}>
                                {translate("Remove Item")}
                            </Link>
                        )}
                        {!isDisabled && (
                            <Link {...styles.addNotes} className="notes-link" onClick={toggleNotesClickHandler}>
                                {toggleNotesHeading}
                            </Link>
                        )}
                        {showNotes && (
                            <TextArea
                                value={notes}
                                rows={3}
                                {...styles.textAreaField}
                                disabled={!quote.canModifyOrder}
                                onChange={notesChangeHandler}
                                onBlur={notesBlurHandler}
                            />
                        )}
                    </StyledWrapper>
                ) : (
                    <StyledWrapper {...styles.notesGridItem}>
                        {lineGroup && lineGroup.notes && (
                            <SmallHeadingAndText heading={translate("Notes")} text={lineGroup.notes} />
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <TwoButtonModal
                modalIsOpen={deleteLineModalIsOpen}
                headlineText={translate("Delete List Item")}
                messageText={`${translate("Are you sure you want to delete this item?")}`}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={() => setDeleteLineModalIsOpen(false)}
                onSubmit={deleteRelatedQuoteLine}
                submitTestSelector="delteRelatedQuoteLine"
                showSubmitBtnLoadingOn={isDeleting}
            />
        </StyledWrapper>
    );
};

/**
 *  the naming is misspelled, it should be related quote lines, the issue is there is another file with the same name but used for SalesRep view
 */
export default connect(mapStateToProps, mapDispatchToProps)(ProposedRelatedQuoteLines);
